import React, { useState } from 'react';
import { useSaaSContext } from '../../store/SaaSContext';
import { formatUnit, parseUnit } from '../../utils/units';
import { 
  STANDARD_BOX_SIZES, 
  getSheetDimensions, 
  getContentGridBoundingBox 
} from '../../utils/pdfBoxes';
import { 
  Box, 
  Link2, 
  Unlink2, 
  Maximize2, 
  Square, 
  Scissors, 
  Layers, 
  HelpCircle 
} from 'lucide-react';

interface TrimBoxMediaBoxPanelProps {
  isModal?: boolean;
  onClose?: () => void;
}

export const TrimBoxMediaBoxPanel: React.FC<TrimBoxMediaBoxPanelProps> = ({ isModal, onClose }) => {
  const { layout, setLayout, template, appUnit } = useSaaSContext();
  const [showInfo, setShowInfo] = useState(false);

  const sheetDims = getSheetDimensions(layout);
  const isLinked = layout.mediaBoxMarginsLinked !== false; // default true

  // Current values or defaults
  const trimW = layout.trimBoxWidth !== undefined ? layout.trimBoxWidth : sheetDims.width;
  const trimH = layout.trimBoxHeight !== undefined ? layout.trimBoxHeight : sheetDims.height;
  const trimOrientation = layout.trimBoxOrientation || (trimW > trimH ? 'landscape' : 'portrait');
  const trimPreset = layout.trimBoxPreset || 'match_sheet';

  const mTop = layout.mediaBoxMarginTop !== undefined ? layout.mediaBoxMarginTop : 14.17;
  const mBottom = layout.mediaBoxMarginBottom !== undefined ? layout.mediaBoxMarginBottom : 14.17;
  const mLeft = layout.mediaBoxMarginLeft !== undefined ? layout.mediaBoxMarginLeft : 14.17;
  const mRight = layout.mediaBoxMarginRight !== undefined ? layout.mediaBoxMarginRight : 14.17;

  const showBoxes = layout.showPdfBoxes !== false; // default to true

  const updateLayout = (updates: Partial<typeof layout>) => {
    setLayout({ ...layout, ...updates });
  };

  // Handle Preset Change
  const handleSizePresetChange = (preset: string) => {
    if (preset === 'match_sheet') {
      updateLayout({
        trimBoxPreset: 'match_sheet',
        trimBoxWidth: sheetDims.width,
        trimBoxHeight: sheetDims.height,
        trimBoxOrientation: layout.pageOrientation || 'portrait'
      });
    } else if (preset === 'fit_content') {
      const bbox = getContentGridBoundingBox(layout, template);
      updateLayout({
        trimBoxPreset: 'fit_content',
        trimBoxWidth: bbox.width,
        trimBoxHeight: bbox.height,
        trimBoxOrientation: bbox.width > bbox.height ? 'landscape' : 'portrait'
      });
    } else if (STANDARD_BOX_SIZES[preset]) {
      const std = STANDARD_BOX_SIZES[preset];
      let w = std.width;
      let h = std.height;
      if (trimOrientation === 'landscape') {
        w = Math.max(std.width, std.height);
        h = Math.min(std.width, std.height);
      } else {
        w = Math.min(std.width, std.height);
        h = Math.max(std.width, std.height);
      }
      updateLayout({
        trimBoxPreset: preset,
        trimBoxWidth: w,
        trimBoxHeight: h
      });
    } else {
      updateLayout({ trimBoxPreset: 'custom' });
    }
  };

  // Orientation toggle
  const handleOrientationChange = (orientation: 'portrait' | 'landscape') => {
    let w = trimW;
    let h = trimH;
    if (orientation === 'landscape' && w < h) {
      w = trimH;
      h = trimW;
    } else if (orientation === 'portrait' && w > h) {
      w = trimH;
      h = trimW;
    }
    updateLayout({
      trimBoxOrientation: orientation,
      trimBoxWidth: w,
      trimBoxHeight: h
    });
  };

  // Margin changes with link support
  const handleMarginChange = (side: 'top' | 'bottom' | 'left' | 'right', valInPt: number) => {
    const val = Math.max(0, valInPt);
    if (isLinked) {
      updateLayout({
        mediaBoxMarginTop: val,
        mediaBoxMarginBottom: val,
        mediaBoxMarginLeft: val,
        mediaBoxMarginRight: val
      });
    } else {
      if (side === 'top') updateLayout({ mediaBoxMarginTop: val });
      if (side === 'bottom') updateLayout({ mediaBoxMarginBottom: val });
      if (side === 'left') updateLayout({ mediaBoxMarginLeft: val });
      if (side === 'right') updateLayout({ mediaBoxMarginRight: val });
    }
  };

  // Fit actions
  const handleFitToContent = () => {
    const bbox = getContentGridBoundingBox(layout, template);
    updateLayout({
      trimBoxPreset: 'fit_content',
      trimBoxWidth: bbox.width,
      trimBoxHeight: bbox.height,
      trimBoxOrientation: bbox.width > bbox.height ? 'landscape' : 'portrait'
    });
  };

  const handleFitToArtboard = () => {
    updateLayout({
      trimBoxPreset: 'match_sheet',
      trimBoxWidth: sheetDims.width,
      trimBoxHeight: sheetDims.height,
      trimBoxOrientation: layout.pageOrientation || 'portrait'
    });
  };

  const handleFitToMargins = () => {
    const marginX = Number(layout.marginX || 0);
    const marginY = Number(layout.marginY || 0);
    const w = Math.max(10, sheetDims.width - marginX * 2);
    const h = Math.max(10, sheetDims.height - marginY * 2);
    updateLayout({
      trimBoxPreset: 'custom',
      trimBoxWidth: w,
      trimBoxHeight: h
    });
  };

  const handleFitMediaToBleed = () => {
    const b = Number(layout.bleed || 8.5);
    updateLayout({
      mediaBoxMarginTop: b,
      mediaBoxMarginBottom: b,
      mediaBoxMarginLeft: b,
      mediaBoxMarginRight: b
    });
  };

  const handleFitMediaToMarksSlug = () => {
    // 28.35 pt = 10mm margin for Slug & Color bars
    updateLayout({
      mediaBoxMarginTop: 28.35,
      mediaBoxMarginBottom: 28.35,
      mediaBoxMarginLeft: 28.35,
      mediaBoxMarginRight: 28.35
    });
  };

  const handleFitMediaToZero = () => {
    updateLayout({
      mediaBoxMarginTop: 0,
      mediaBoxMarginBottom: 0,
      mediaBoxMarginLeft: 0,
      mediaBoxMarginRight: 0
    });
  };

  const handleFitArtboardToTrim = () => {
    updateLayout({
      pageSize: 'custom',
      pageWidth: trimW,
      pageHeight: trimH,
      pageOrientation: trimOrientation
    });
  };

  const handleFitArtboardToMedia = () => {
    const totalW = trimW + mLeft + mRight;
    const totalH = trimH + mTop + mBottom;
    updateLayout({
      pageSize: 'custom',
      pageWidth: totalW,
      pageHeight: totalH,
      pageOrientation: totalW > totalH ? 'landscape' : 'portrait'
    });
  };

  return (
    <div className="bg-zinc-900 border border-zinc-700/80 rounded-lg shadow-xl overflow-hidden text-zinc-200 text-xs font-sans">
      {/* Header matching Adobe Prepress Tool */}
      <div className="bg-zinc-800/90 px-3 py-2 border-b border-zinc-700/80 flex items-center justify-between select-none">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse"></div>
          <span className="font-semibold text-zinc-100 text-xs tracking-wide">Trim Box and Media Box</span>
        </div>
        <div className="flex items-center gap-1">
          <button 
            type="button"
            onClick={() => setShowInfo(!showInfo)}
            className={`p-1 rounded hover:bg-zinc-700 transition-colors ${showInfo ? 'text-blue-400 bg-zinc-700' : 'text-zinc-400'}`}
            title="Prepress Box Definitions"
          >
            <HelpCircle className="w-3.5 h-3.5" />
          </button>
          {onClose && (
            <button 
              type="button"
              onClick={onClose}
              className="p-1 rounded hover:bg-zinc-700 text-zinc-400 hover:text-zinc-100"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {showInfo && (
        <div className="p-3 bg-blue-950/40 border-b border-blue-900/60 text-[11px] text-blue-200/90 leading-relaxed space-y-1">
          <p><strong>Trim Box (Solid Line):</strong> Defines the finished cut size of the document/sheet after guillotining or cutting.</p>
          <p><strong>Media Box (Dotted Blue Line):</strong> Defines the physical medium boundaries (press sheet / paper dimensions) including slug and margin areas.</p>
        </div>
      )}

      <div className="p-3.5 space-y-3.5">
        {/* Section 1: Trim Box (Borders) */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="font-semibold text-zinc-200 text-xs flex items-center gap-1.5">
              <Scissors className="w-3.5 h-3.5 text-blue-400" />
              Trim Box (Borders):
            </label>
            <span className="text-[10px] text-zinc-400 font-mono">
              {formatUnit(trimW, appUnit)} × {formatUnit(trimH, appUnit)} {appUnit}
            </span>
          </div>

          <div className="space-y-2 bg-zinc-950/70 p-2.5 rounded-md border border-zinc-800">
            {/* Size Dropdown */}
            <div className="flex items-center gap-2">
              <span className="w-12 text-[11px] text-zinc-400">Size:</span>
              <select
                className="flex-1 px-2 py-1 bg-zinc-900 border border-zinc-700 text-zinc-100 rounded text-xs outline-none focus:border-blue-500 font-medium"
                value={trimPreset}
                onChange={(e) => handleSizePresetChange(e.target.value)}
              >
                <option value="match_sheet">Match Sheet Size ({layout.pageSize})</option>
                <option value="fit_content">Fit to Content / Stickers Grid</option>
                <option value="custom">Custom Dimensions</option>
                <optgroup label="Standard Press Formats">
                  <option value="A4">A4 (210 × 297 mm)</option>
                  <option value="A3">A3 (297 × 420 mm)</option>
                  <option value="SRA3">SRA3 (320 × 450 mm)</option>
                  <option value="letter">US Letter (8.5 × 11 in)</option>
                  <option value="tabloid">US Tabloid (11 × 17 in)</option>
                  <option value="12x18">Digital 12 × 18 in</option>
                  <option value="13x19">Super B 13 × 19 in</option>
                </optgroup>
              </select>
            </div>

            {/* Width, Height, Orientation */}
            <div className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-8 grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-zinc-400 block mb-0.5">Width ({appUnit}):</label>
                  <input
                    type="number"
                    step="0.5"
                    min="1"
                    className="w-full px-2 py-1 bg-zinc-900 border border-zinc-700 text-zinc-100 rounded text-xs font-mono outline-none focus:border-blue-500"
                    value={formatUnit(trimW, appUnit)}
                    onChange={(e) => {
                      const val = parseUnit(e.target.value, appUnit);
                      updateLayout({
                        trimBoxPreset: 'custom',
                        trimBoxWidth: Math.max(1, val)
                      });
                    }}
                  />
                </div>
                <div>
                  <label className="text-[10px] text-zinc-400 block mb-0.5">Height ({appUnit}):</label>
                  <input
                    type="number"
                    step="0.5"
                    min="1"
                    className="w-full px-2 py-1 bg-zinc-900 border border-zinc-700 text-zinc-100 rounded text-xs font-mono outline-none focus:border-blue-500"
                    value={formatUnit(trimH, appUnit)}
                    onChange={(e) => {
                      const val = parseUnit(e.target.value, appUnit);
                      updateLayout({
                        trimBoxPreset: 'custom',
                        trimBoxHeight: Math.max(1, val)
                      });
                    }}
                  />
                </div>
              </div>

              {/* Orientation Buttons [A] [A rotated] */}
              <div className="col-span-4 flex flex-col items-center">
                <span className="text-[10px] text-zinc-400 block mb-0.5">Orientation:</span>
                <div className="flex items-center gap-1 bg-zinc-900 p-0.5 rounded border border-zinc-700">
                  <button
                    type="button"
                    onClick={() => handleOrientationChange('portrait')}
                    className={`w-7 h-6 flex items-center justify-center font-bold text-xs rounded transition-colors ${
                      trimOrientation === 'portrait' 
                        ? 'bg-blue-600 text-white shadow-xs' 
                        : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
                    }`}
                    title="Portrait"
                  >
                    A
                  </button>
                  <button
                    type="button"
                    onClick={() => handleOrientationChange('landscape')}
                    className={`w-7 h-6 flex items-center justify-center font-bold text-xs rounded transition-colors ${
                      trimOrientation === 'landscape' 
                        ? 'bg-blue-600 text-white shadow-xs' 
                        : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
                    }`}
                    title="Landscape"
                  >
                    <span className="inline-block transform -rotate-90">A</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Fit to buttons row */}
            <div className="flex items-center gap-1 pt-1">
              <span className="text-[10px] text-zinc-400 mr-1">Fit to:</span>
              <button
                type="button"
                onClick={handleFitToContent}
                className="px-2 py-1 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white rounded border border-zinc-700 text-[10px] flex items-center gap-1"
                title="Fit Trim Box to Content / Stickers Grid"
              >
                <Square className="w-3 h-3 text-blue-400" />
                Artwork
              </button>
              <button
                type="button"
                onClick={handleFitToArtboard}
                className="px-2 py-1 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white rounded border border-zinc-700 text-[10px] flex items-center gap-1"
                title="Fit Trim Box to Sheet Size"
              >
                <Maximize2 className="w-3 h-3 text-emerald-400" />
                Sheet
              </button>
              <button
                type="button"
                onClick={handleFitToMargins}
                className="px-2 py-1 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white rounded border border-zinc-700 text-[10px] flex items-center gap-1"
                title="Fit Trim Box inside Sheet Margins"
              >
                <Layers className="w-3 h-3 text-amber-400" />
                Margins
              </button>
            </div>
          </div>
        </div>

        {/* Section 2: Media Box (Margins) */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="font-semibold text-zinc-200 text-xs flex items-center gap-1.5">
              <Box className="w-3.5 h-3.5 text-blue-400" />
              Media Box (Margins):
            </label>
            <span className="text-[10px] text-zinc-400 font-mono">
              Outer: {formatUnit(trimW + mLeft + mRight, appUnit)} × {formatUnit(trimH + mTop + mBottom, appUnit)} {appUnit}
            </span>
          </div>

          <div className="bg-zinc-950/70 p-2.5 rounded-md border border-zinc-800 space-y-2">
            <div className="flex items-center justify-center gap-2">
              {/* Left Column: Top and Bottom */}
              <div className="space-y-1.5 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="w-10 text-[10px] text-zinc-400">Top:</span>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    className="w-full px-1.5 py-1 bg-zinc-900 border border-zinc-700 text-zinc-100 rounded text-xs font-mono outline-none focus:border-blue-500"
                    value={formatUnit(mTop, appUnit)}
                    onChange={(e) => handleMarginChange('top', parseUnit(e.target.value, appUnit))}
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-10 text-[10px] text-zinc-400">Bottom:</span>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    className="w-full px-1.5 py-1 bg-zinc-900 border border-zinc-700 text-zinc-100 rounded text-xs font-mono outline-none focus:border-blue-500"
                    value={formatUnit(mBottom, appUnit)}
                    onChange={(e) => handleMarginChange('bottom', parseUnit(e.target.value, appUnit))}
                  />
                </div>
              </div>

              {/* Center Link Button */}
              <button
                type="button"
                onClick={() => updateLayout({ mediaBoxMarginsLinked: !isLinked })}
                className={`p-2 rounded border transition-colors ${
                  isLinked 
                    ? 'bg-blue-600/30 border-blue-500 text-blue-300' 
                    : 'bg-zinc-900 border-zinc-700 text-zinc-500 hover:text-zinc-300'
                }`}
                title={isLinked ? 'Margins Linked (equal on all sides)' : 'Margins Unlinked'}
              >
                {isLinked ? <Link2 className="w-4 h-4" /> : <Unlink2 className="w-4 h-4" />}
              </button>

              {/* Right Column: Left and Right */}
              <div className="space-y-1.5 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="w-10 text-[10px] text-zinc-400">Left:</span>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    className="w-full px-1.5 py-1 bg-zinc-900 border border-zinc-700 text-zinc-100 rounded text-xs font-mono outline-none focus:border-blue-500"
                    value={formatUnit(mLeft, appUnit)}
                    onChange={(e) => handleMarginChange('left', parseUnit(e.target.value, appUnit))}
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-10 text-[10px] text-zinc-400">Right:</span>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    className="w-full px-1.5 py-1 bg-zinc-900 border border-zinc-700 text-zinc-100 rounded text-xs font-mono outline-none focus:border-blue-500"
                    value={formatUnit(mRight, appUnit)}
                    onChange={(e) => handleMarginChange('right', parseUnit(e.target.value, appUnit))}
                  />
                </div>
              </div>
            </div>

            {/* Margin Presets */}
            <div className="flex items-center gap-1 pt-1 border-t border-zinc-800/80">
              <span className="text-[10px] text-zinc-400 mr-1">Fit to:</span>
              <button
                type="button"
                onClick={handleFitMediaToBleed}
                className="px-2 py-0.5 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 rounded border border-zinc-700 text-[10px]"
                title="Set Media Box margins equal to Bleed"
              >
                Bleed
              </button>
              <button
                type="button"
                onClick={handleFitMediaToMarksSlug}
                className="px-2 py-0.5 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 rounded border border-zinc-700 text-[10px]"
                title="Add 10mm margins for Marks & Slug"
              >
                Marks & Slug
              </button>
              <button
                type="button"
                onClick={handleFitMediaToZero}
                className="px-2 py-0.5 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 rounded border border-zinc-700 text-[10px]"
                title="Set 0 Margins (Media Box = Trim Box)"
              >
                0 Margin
              </button>
            </div>
          </div>
        </div>

        {/* Section 3: Artboard Sync */}
        <div>
          <label className="font-semibold text-zinc-300 text-xs block mb-1">
            Artboard / Sheet Sync:
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleFitArtboardToTrim}
              className="flex-1 py-1 px-2 bg-zinc-950 hover:bg-zinc-800 text-zinc-200 rounded border border-zinc-700 text-xs text-center transition-colors"
            >
              Fit Artboard to Trim Box
            </button>
            <button
              type="button"
              onClick={handleFitArtboardToMedia}
              className="flex-1 py-1 px-2 bg-zinc-950 hover:bg-zinc-800 text-zinc-200 rounded border border-zinc-700 text-xs text-center transition-colors"
            >
              Fit Artboard to Media Box
            </button>
          </div>
        </div>

        {/* Section 4: Show Checkbox & PDF Standard Option */}
        <div className="pt-2 border-t border-zinc-800 space-y-2">
          <label className="flex items-center gap-2 cursor-pointer select-none text-xs text-zinc-200 font-medium">
            <input
              type="checkbox"
              className="w-4 h-4 rounded text-blue-600 bg-zinc-950 border-zinc-700 focus:ring-blue-500"
              checked={showBoxes}
              onChange={(e) => updateLayout({ showPdfBoxes: e.target.checked })}
            />
            <span>Show Trim Box and Media Box</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer select-none text-[11px] text-zinc-400 hover:text-zinc-200">
            <input
              type="checkbox"
              className="w-3.5 h-3.5 rounded text-blue-600 bg-zinc-950 border-zinc-700 focus:ring-blue-500"
              checked={layout.enablePdfPageBoxes !== false}
              onChange={(e) => updateLayout({ enablePdfPageBoxes: e.target.checked })}
            />
            <span>Embed ISO 32000 PDF Page Boxes in PDF Exports</span>
          </label>
        </div>
      </div>

      {/* Footer Action Buttons */}
      <div className="pt-2 border-t border-zinc-800 flex items-center justify-between gap-2">
        <span className="text-[10px] text-zinc-500 font-mono">ISO 32000 PDF Boxes Active</span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg shadow-sm transition-colors cursor-pointer"
          >
            Apply & Close
          </button>
        )}
      </div>
    </div>
  );
};
